import { createContext, useCallback, useEffect, useReducer } from 'react';
import type { FC } from 'react';
import { BoothIdName, initialVendor, Vendor } from '../types/vendor';
import { restoreVendor, storeVendor, vendorReducer } from './VendorContextFunctions';
import { ContextProviderProps } from 'src/types/contextProviderProps';
import UmbarcoApi from 'src/api/UmbarcoApi';
import VendorApi from 'src/api/VendorApi';
import { VendorBooth } from 'src/types/vendorBooth';
import { CategoryManager } from 'src/types/categoryManager';

export interface IVendorContext {
  vendor: Vendor;
  saveVendor: (vendor: Vendor) => void;
  setSelectedBoothId: (boothId: number) => void;
  setVendorBoothsDropdown: (booths: VendorBooth[]) => void;
}

const VendorContext = createContext<IVendorContext>({
  vendor: { ...initialVendor },
  saveVendor: () => {},
  setSelectedBoothId: () => {},
  setVendorBoothsDropdown: () => {},
});

export const VendorProvider: FC<ContextProviderProps> = ({ children }) => {
  const [vendor, dispatch] = useReducer(vendorReducer, initialVendor);

  useEffect(() => {
    const restoredVendor = restoreVendor();
    if (restoredVendor) {
      dispatch({
        type: 'SetVendor',
        payload: {
          Vendor: restoredVendor,
        },
      });
    }
  }, []);

  const saveVendor = useCallback(async (updatedVendor: Vendor): Promise<void> => {
    let v = { ...updatedVendor };
    v.boothDropdownLoading = true;

    dispatch({
      type: 'SetVendor',
      payload: v,
    });

    let v2 = { ...v };
    let vendorBooths: VendorBooth[] = await UmbarcoApi.getBoothsByVendor(updatedVendor.number);
    let categoryManagers: CategoryManager[] = await VendorApi.getCategoryManagers(updatedVendor.number);

    v2.boothsDropdown = vendorBooths.map<BoothIdName>((vb) => {
      return {
        boothId: vb.id,
        name: vb.name,
      };
    });

    v2.categoryManagers = categoryManagers;
    v2.boothDropdownLoading = false;

    dispatch({
      type: 'SetVendor',
      payload: { ...v2 },
    });

    storeVendor(updatedVendor);
  }, []);

  const setSelectedBoothId = useCallback(
    (boothId: number) => {
      let v = { ...vendor };
      v.selectedBoothId = boothId;
      dispatch({
        type: 'SetVendor',
        payload: v,
      });
    },
    [vendor]
  );

  const setVendorBoothsDropdown = useCallback(
    (booths: VendorBooth[]) => {
      let v = { ...vendor };

      v.boothsDropdown = booths.map<BoothIdName>((vb) => {
        return {
          boothId: vb.id,
          name: vb.name,
        };
      });

      dispatch({
        type: 'SetVendor',
        payload: v,
      });
    },
    [vendor]
  );

  return (
    <VendorContext.Provider
      value={{
        vendor,
        saveVendor,
        setSelectedBoothId,
        setVendorBoothsDropdown,
      }}
    >
      {children}
    </VendorContext.Provider>
  );
};

export default VendorContext;
